import React, { useState, useRef, useEffect, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { Button, FormControl, MenuItem, Select, Autocomplete, TextField } from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";

import { CustomAutocomplete } from "@components/custom_autocomplete";

import { useAuthStore } from "@user/store/auth_store";

interface State {
  trNoInfos: Code[],
  snInfos: Code[]
}

const defState: State = {
  trNoInfos: [],
  snInfos: []
};

interface Info {
  workReqErpNo: string,
  pn: string,
  prevTrNo: string,
  prevSn: string,
  trSn: string,
  trNo: string,
  sn: string
}

const defInfo: Info = {
  workReqErpNo: "",
  pn: "",
  prevTrNo: "",
  prevSn: "",
  trSn: "",
  trNo: "",
  sn: ""
};

const validationMsg = [
  {id: "eaiId", type: "InvalidStatus", msg: (_p: any, e: any) => `${e.desc}`},
  {id: "workReqErpNo", type: "InvalidStatus", msg: "이미 작업완료된 작업은 변경할 수 없습니다."}
];

function PopupTrSnChange(props: any) {
  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [info, setInfo] = useState<Info>(kutil.deepCopy(defInfo));

  useEffect(() => {
    init();
  }, []);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfo();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const info = props.data.info;
    setInfo({ ...info, prevTrNo: info.trNo, prevSn: info.sn, trNo: "", sn: "" });
  }

  async function searchInfo() {
    if (!props.data.info.pn) {
      return;
    }

    try {
      const data = await kutil.sendGet("/api/inoutbound/" + props.data.path + ".data?mode=searchTrSn", {wh: props.data.info.wh, pn: props.data.info.pn}, {});
      (data.trNoInfos as any[]).forEach((item) => {
        item.code = item.trNo;
        item.value = item.trNo;
      });
      (data.snInfos as any[]).forEach((item) => {
        item.code = item.sn;
        item.value = item.sn;
      });
      setState(prev => ({
        ...prev,
        trNoInfos: data.trNoInfos,
        snInfos: data.snInfos,
      }));
    } catch(e) {
      console.log(e);
    }
  }

  async function closePopup() {
    props.onClose();
  }

  async function save() {
    if (!info.trSn) {
      kutil.alert("TR No, S/N를 선택해 주세요.");
      return;
    }

    if (await kutil.confirm("변경하시겠습니까?") == false) {
      return false;
    }

    try {
      const data = await kutil.sendPost("/api/inoutbound/" + props.data.path + ".proc?mode=updateTrSn", info, {
        validationMsg: validationMsg
      });
      if (data.ifCode) {
        await kutil.alert("변경하였습니다.");
        props.onClose({isSuccess: true});
      } else {
        await kutil.alert("변경된 내용이 없습니다.");
      }
    } catch(e) {
      console.log(e);
    }
  }

  const onChangeTrNo = useCallback((code: string) => {
    setInfo((prev) => ({ ...prev, trNo: code, sn: "", trSn: code }));
  }, []);

  const onChangeSn = useCallback((code: string) => {
    setInfo((prev) => ({ ...prev, trNo: "", sn: code, trSn: code }));
  }, []);

  return (
    <>
      {/* 팝업 */}
      <Dialog
        sx={{ "& .MuiDialog-container": { "& .MuiPaper-root": { width: "100%", maxWidth: "700px", }, }, }}
        aria-labelledby="customized-dialog-title"
        open={props.isShow}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          TR No, S/N 변경
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={closePopup}
          sx={(theme) => ({
            position: "absolute",
            right: 36,
            top: 24,
            color: theme.palette.grey[500],
          })}
        >
        </IconButton>
        <DialogContent>
          {/* 팝업 내용 */}
          <div className="popup-contents">
            {/* TR No, S/N 변경 */}
            <div className="table-wrap">
              <table>
                <colgroup>
                  <col style={{ width: "17%" }} />
                  <col style={{ width: "33%" }} />
                  <col style={{ width: "17%" }} />
                  <col style={{ width: "33%" }} />
                </colgroup>
                <tbody>
                  <tr>
                    <th scope="row">P/N</th>
                    <td colSpan={3}>
                      { info.pn }
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">TR No</th>
                    <td>
                      { info.prevTrNo }
                    </td>
                    <th scope="row">S/N</th>
                    <td>
                      { info.prevSn }
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">변경 TR No</th>
                    <td>
                      <CustomAutocomplete
                        options={state.trNoInfos}
                        value={info.trNo}
                        onChange={onChangeTrNo}
                      />
                    </td>
                    <th scope="row">변경 S/N</th>
                    <td>
                      <CustomAutocomplete
                        options={state.snInfos}
                        value={info.sn}
                        onChange={onChangeSn}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            {/*// TR No, S/N 변경 */}
          </div>
          {/*// 팝업 내용 */}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" autoFocus onClick={closePopup}>
            취소
          </Button>
          { auth.write &&
          <Button variant="contained" autoFocus onClick={save}>
            변경
          </Button>
          }
        </DialogActions>
      </Dialog>
      {/*// 팝업 */}
    </>
  );
}

export default React.memo(PopupTrSnChange);
